import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams, useHistory } from "react-router-dom";

import { Snackbar } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import AccountTreeIcon from "@material-ui/icons/AccountTree";
import AssessmentIcon from "@material-ui/icons/Assessment";
import CalculatorIcon from "../components/layout/CalculatorIcon";
import HaklaiCalc from "../components/haklai/calc/HaklaiCalcView";
import HaklaiAnalysisResultsView from "../components/haklai/results/HaklaiAnalysisResultsView";
import ManageComponent from "../components/shared/manageComponent";
import HaklaiTabContainer from "../components/haklai/HaklaiTabContainer";
import {
  createInitialHaklaiCalc,
  initialHaklaiManage
} from "../components/haklai/domain/haklaiInitialState";
import { hydrateHaklaiDealData } from "../components/haklai/domain/haklaiDealHydration";
import { isHaklaiAnalysisFullyExempt } from "../components/haklai/domain/haklaiAnalysisOutcome";
import {
  validateHaklaiForAnalyze
} from "../components/haklai/domain/haklaiValidation";
import useHaklaiCalcActions from "../components/haklai/hooks/useHaklaiCalcActions";
import useHaklaiNotice from "../components/haklai/hooks/useHaklaiNotice";
import useHaklaiSaveAnalyze from "../components/haklai/hooks/useHaklaiSaveAnalyze";
import useHaklaiUsdRate from "../components/haklai/hooks/useHaklaiUsdRate";
import { getHaklaiDealData } from "../components/haklai/services/haklaiDealService";

const topNavOpts = {
  CALC: "CALC",
  RESULTS: "RESULTS",
  MANAGE: "MANAGE"
};

const HAKLAI_NOTICE_AUTO_HIDE_DURATION = 5000;
const HAKLAI_NOTICE_MESSAGES = {
  needsAnalysis: "יש להריץ ניתוח כדי לראות תוצאות.",
  noCalculationOutput: "העסקה אינה חייבת במס ואין שומות חישוב להפקה.",
  returningToCalc: "לתשומת ליבך: שינוי נתונים במסך החישוב ידרוש הרצת ניתוח מחדש."
};

const HaklaiContainer = () => {
  // Router hooks
  const { dealId: dealIdParam, tab } = useParams();
  const history = useHistory();

  // State hooks
  const [dealId, setDealId] = useState(null);
  const [prettyId, setPrettyId] = useState(null);
  const [dealDate, setDealDate] = useState("");
  const [isDealDateChanged, setIsDealDateChanged] = useState(false);
  const [currentNavFocused, setCurrentNavFocused] = useState(topNavOpts.CALC);
  const [calc, setCalc] = useState(() => createInitialHaklaiCalc());
  const [haklaiAnalysis, setHaklaiAnalysis] = useState(null);
  const [analyzedClassicCalc, setAnalyzedClassicCalc] = useState({ preCalc: null, mainCalc: null });
  const [isHaklaiAnalysisCurrent, setIsHaklaiAnalysisCurrent] = useState(false);
  const currentHaklaiAnalysisCalcRef = useRef(null);
  const [manage, setManage] = useState(initialHaklaiManage);
  const [didLoadDealData, setDidLoadDealData] = useState(false);
  const {
    closeHaklaiNotice,
    dismissHaklaiNotice,
    haklaiNotice,
    showHaklaiNotice
  } = useHaklaiNotice();

  // ── Fetch deal data on mount ─────────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    setDidLoadDealData(false);
    setIsHaklaiAnalysisCurrent(false);
    currentHaklaiAnalysisCalcRef.current = null;
    window.scrollTo({ top: 0, behavior: "smooth" });
    const dealId = dealIdParam;

    if (!dealId) {
      history.replace("/my-deals");
      return;
    }

    getHaklaiDealData(dealId).then((res) => {
      if (cancelled) return;
      if (!res.success || !res.dealData || res.dealData.softwareServiceType !== "btaxHaklai") {
        history.replace("/my-deals");
        return;
      }

      const hydratedDeal = hydrateHaklaiDealData(res.dealData);
      setDealId(dealId);
      setPrettyId(hydratedDeal.prettyId);
      setManage(hydratedDeal.manage);
      setHaklaiAnalysis(hydratedDeal.haklaiAnalysis);
      setAnalyzedClassicCalc(hydratedDeal.analyzedClassicCalc);
      setCalc(hydratedDeal.calc);
      const hasCurrentAnalysis = Boolean(hydratedDeal.hasSavedHaklaiAnalysis && hydratedDeal.haklaiAnalysis);
      currentHaklaiAnalysisCalcRef.current = hasCurrentAnalysis ? hydratedDeal.calc : null;
      setIsHaklaiAnalysisCurrent(hasCurrentAnalysis);
      setDealDate(hydratedDeal.dealDate);
      setDidLoadDealData(true);
    }).catch(() => {
      if (!cancelled) {
        history.replace("/my-deals");
      }
    });

    return () => {
      cancelled = true;
    };
  }, [dealIdParam, history]);

  const markHaklaiAnalysisCurrent = useCallback((sourceCalc) => {
    currentHaklaiAnalysisCalcRef.current = sourceCalc || null;
    setIsHaklaiAnalysisCurrent(Boolean(sourceCalc));
  }, []);

  useEffect(() => {
    if (!isHaklaiAnalysisCurrent) return;
    if (currentHaklaiAnalysisCalcRef.current === calc) return;
    currentHaklaiAnalysisCalcRef.current = null;
    setIsHaklaiAnalysisCurrent(false);
  }, [calc, isHaklaiAnalysisCurrent]);

  const hasCurrentHaklaiAnalysis = Boolean(
    haklaiAnalysis
    && isHaklaiAnalysisCurrent
    && currentHaklaiAnalysisCalcRef.current === calc
  );
  const showNoTaxResult = Boolean(
    hasCurrentHaklaiAnalysis
    && isHaklaiAnalysisFullyExempt(analyzedClassicCalc.preCalc)
  );
  // Update current nav focused based on URL
  useEffect(() => {
    if (!didLoadDealData) return;

    if (tab?.toUpperCase() === topNavOpts.RESULTS && !hasCurrentHaklaiAnalysis) {
      setCurrentNavFocused(topNavOpts.CALC);
      history.replace("/haklai/calc/" + dealId);
      return;
    }

    if (!tab || !topNavOpts[tab.toUpperCase()]) {
      setCurrentNavFocused(topNavOpts.CALC);
      history.replace("/haklai/calc/" + dealId);
      return;
    }

    if (tab && topNavOpts[tab.toUpperCase()]) {
      const newNavFocused = topNavOpts[tab.toUpperCase()];
      if (currentNavFocused !== newNavFocused) {
        if (newNavFocused === topNavOpts.RESULTS) {
          dismissHaklaiNotice();
        }
        if (currentNavFocused === topNavOpts.RESULTS && newNavFocused === topNavOpts.CALC && hasCurrentHaklaiAnalysis) {
          showHaklaiNotice(HAKLAI_NOTICE_MESSAGES.returningToCalc);
        }
        setCurrentNavFocused(newNavFocused);
      }
    }
  }, [
    tab,
    currentNavFocused,
    didLoadDealData,
    hasCurrentHaklaiAnalysis,
    history,
    dealId,
    dismissHaklaiNotice,
    showHaklaiNotice
  ]);

  const {
    acquisitionCostInShekel,
    conversionDate,
    usdRate,
    usdRateLoading,
    usdRateStale
  } = useHaklaiUsdRate({ calc, setCalc });

  // Tab navigation handler
  const onTopNavChanged = useCallback(
    (newState) => {
      if (newState === currentNavFocused) {
        return;
      }
      if (
        newState === topNavOpts.RESULTS
        && (!hasCurrentHaklaiAnalysis || showNoTaxResult)
      ) {
        return;
      }
      if (newState === topNavOpts.RESULTS) {
        dismissHaklaiNotice();
      }
      if (currentNavFocused === topNavOpts.RESULTS && newState === topNavOpts.CALC && hasCurrentHaklaiAnalysis) {
        showHaklaiNotice(HAKLAI_NOTICE_MESSAGES.returningToCalc);
      }
      setCurrentNavFocused(newState);
      history.push("/haklai/" + newState.toLowerCase() + "/" + dealId);
    },
    [
      currentNavFocused,
      dealId,
      dismissHaklaiNotice,
      hasCurrentHaklaiAnalysis,
      history,
      showNoTaxResult,
      showHaklaiNotice
    ]
  );

  const backToCalcPage = useCallback(() => {
    history.push("/haklai/calc/" + dealId);
  }, [history, dealId]);

  const validateForAnalyze = useCallback((sourceCalc = calc) => (
    validateHaklaiForAnalyze({ calc: sourceCalc, conversionDate, usdRate })
  ), [calc, conversionDate, usdRate]);

  const {
    analyzeDeal,
    haklaiActionInFlight,
    prepareAnalyzeCalc,
    updateDealData,
    updateManageData
  } = useHaklaiSaveAnalyze({
    calc,
    dealId,
    dismissHaklaiNotice,
    history,
    isDealDateChanged,
    manage,
    resultsNavKey: topNavOpts.RESULTS,
    setAnalyzedClassicCalc,
    setCalc,
    markHaklaiAnalysisCurrent,
    setCurrentNavFocused,
    setHaklaiAnalysis,
    setIsDealDateChanged,
    validateForAnalyze
  });

  const {
    anotherQuestionsFieldErrors,
    handleAcquisitionDetailsOnChange,
    handleAddExpense,
    handleAddRow,
    handleExpenseOnChange,
    handleMainCalcAnotherQuestionsOnChange,
    handleMainCalcPrisaOnChange,
    handlePrisaCreditPointsApply,
    handlePreCalcOnChange,
    handlePropertyPartOnChange,
    handlePurchaseDepreciationChange,
    handlePurchaseDepreciationSelectionChange,
    handlePurchaseZabanAttributionChange,
    handleRemoveExpense,
    handleRemoveRow,
    prisaFieldErrors,
    touchPrisaField
  } = useHaklaiCalcActions({
    calc,
    setCalc,
    setDealDate,
    setIsDealDateChanged
  });

  // ── Handle manage onChange ───────────────────────────────────────
  const handleManageOnChange = useCallback(
    (e) => {
      let val = e.target.value;
      if (val === "true") val = true;
      if (val === "false") val = false;
      setManage((prev) => ({ ...prev, [e.target.name]: val }));
    },
    []
  );

  // ── Create containerTabs ─────────────────────────────────────────
  const containerTabs = useMemo(() => {
    return {
      currentNavFocused,
      tabs: [
        {
          key: topNavOpts.CALC,
          text: "חישוב מס שבח",
          icon: <CalculatorIcon color="secondary" />,
          path: "/haklai/calc/:dealId",
          component: (props) => (
            <HaklaiCalc
              {...props}
              calc={calc}
              acquisitionCostInShekel={acquisitionCostInShekel}
              usdRate={usdRate}
              usdRateLoading={usdRateLoading}
              usdRateStale={usdRateStale}
              handlePreCalcOnChange={handlePreCalcOnChange}
              handleAcquisitionDetailsOnChange={handleAcquisitionDetailsOnChange}
              handlePropertyPartOnChange={handlePropertyPartOnChange}
              handleAddRow={handleAddRow}
              handleRemoveRow={handleRemoveRow}
              handleExpenseOnChange={handleExpenseOnChange}
              handleAddExpense={handleAddExpense}
              handleRemoveExpense={handleRemoveExpense}
              handlePurchaseDepreciationChange={handlePurchaseDepreciationChange}
              handlePurchaseDepreciationSelectionChange={handlePurchaseDepreciationSelectionChange}
              handlePurchaseZabanAttributionChange={handlePurchaseZabanAttributionChange}
              handleMainCalcPrisaOnChange={handleMainCalcPrisaOnChange}
              handlePrisaCreditPointsApply={handlePrisaCreditPointsApply}
              handleMainCalcAnotherQuestionsOnChange={handleMainCalcAnotherQuestionsOnChange}
              prisaFieldErrors={prisaFieldErrors}
              anotherQuestionsFieldErrors={anotherQuestionsFieldErrors}
              touchPrisaField={touchPrisaField}
              updateDealData={updateDealData}
              analyzeDeal={analyzeDeal}
              getAnalyzeValidationError={validateForAnalyze}
              prepareAnalyzeCalc={prepareAnalyzeCalc}
              isActionInFlight={haklaiActionInFlight}
              showNoTaxResult={showNoTaxResult}
            />
          ),
          stateFamily: "calc",
          functions: {
            updateDealData,
            analyzeDeal
          }
        },
        {
          key: topNavOpts.RESULTS,
          text: "שומות להגשה",
          icon: <AssessmentIcon color="secondary" />,
          isDisabled: !hasCurrentHaklaiAnalysis,
          isNavigationDisabled: showNoTaxResult,
          disabledTooltip: showNoTaxResult
            ? HAKLAI_NOTICE_MESSAGES.noCalculationOutput
            : HAKLAI_NOTICE_MESSAGES.needsAnalysis,
          path: "/haklai/results/:dealId",
          component: () => (
            <HaklaiAnalysisResultsView
              dealId={dealId}
              haklaiAnalysis={haklaiAnalysis}
              haklaiDraftPreCalc={calc.preCalc}
              analyzedPreCalc={analyzedClassicCalc.preCalc}
              analyzedMainCalc={analyzedClassicCalc.mainCalc}
              onBackToCalc={backToCalcPage}
            />
          ),
          stateFamily: "calc",
          functions: {}
        },
        {
          key: topNavOpts.MANAGE,
          text: "ניהול עסקה",
          icon: <AccountTreeIcon color="secondary" />,
          path: "/haklai/manage/:dealId",
          component: (props) => <ManageComponent {...props} isFullManageComponent={true} dealDate={dealDate} />,
          stateFamily: "manage",
          functions: { handleManageOnChange, updateDealData: updateManageData }
        }
      ]
    };
  }, [
    dealId,
    currentNavFocused,
    hasCurrentHaklaiAnalysis,
    dealDate,
    handleManageOnChange,
    updateDealData,
    updateManageData,
    analyzeDeal,
    haklaiActionInFlight,
    showNoTaxResult,
    calc,
    acquisitionCostInShekel,
    usdRate,
    usdRateLoading,
    usdRateStale,
    handlePreCalcOnChange,
    handleAcquisitionDetailsOnChange,
    handlePropertyPartOnChange,
    handleAddRow,
    handleRemoveRow,
    handleExpenseOnChange,
    handleAddExpense,
    handleRemoveExpense,
    handlePurchaseDepreciationChange,
    handlePurchaseDepreciationSelectionChange,
    handlePurchaseZabanAttributionChange,
    handleMainCalcPrisaOnChange,
    handlePrisaCreditPointsApply,
    handleMainCalcAnotherQuestionsOnChange,
    prisaFieldErrors,
    anotherQuestionsFieldErrors,
    touchPrisaField,
    validateForAnalyze,
    prepareAnalyzeCalc,
    haklaiAnalysis,
    analyzedClassicCalc,
    backToCalcPage
  ]);

  return (
    <>
      {dealId ? (
        <>
          <HaklaiTabContainer
            title={prettyId ? "מספר עסקה " + prettyId : ""}
            subTitle={manage ? manage.dealDescription : ""}
            calc={calc}
            manage={manage}
            optionalTabs={containerTabs.tabs}
            currentNavFocused={containerTabs.currentNavFocused}
            onTopNavChanged={onTopNavChanged}
          />
          <Snackbar
            open={haklaiNotice.open}
            autoHideDuration={HAKLAI_NOTICE_AUTO_HIDE_DURATION}
            onClose={closeHaklaiNotice}
            onClick={closeHaklaiNotice}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          >
            <Alert onClose={closeHaklaiNotice} severity="info">
              {haklaiNotice.message}
            </Alert>
          </Snackbar>
        </>
      ) : null}
    </>
  );
};

export default HaklaiContainer;
